import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Send, Check, UserPlus, Pencil, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const emptyLead = { nom: "", prenom: "", email: "", telephone: "" };

export default function TeamRequestModal({ open, onOpenChange, concertId, role }) {
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyLead);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [candQ, setCandQ] = useState("");
  const [cands, setCands] = useState([]);
  const [candOpen, setCandOpen] = useState(false);
  const candTimer = useRef(null);

  const fetchCands = (text) => {
    setCandQ(text);
    if (candTimer.current) clearTimeout(candTimer.current);
    if (!text || text.trim().length < 1) { setCands([]); return; }
    candTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/team-lead-candidates", { params: { role, q: text } });
        setCands(data || []); setCandOpen(true);
      } catch { setCands([]); }
    }, 200);
  };

  const pickCandidate = (cd) => {
    setForm({ nom: cd.nom || "", prenom: cd.prenom || "", email: cd.email || "", telephone: cd.telephone || "" });
    setCandQ([cd.nom, cd.prenom].filter(Boolean).join(" "));
    setCandOpen(false);
  };

  const label = role === "chef_hotesse" ? "Chef Hôtesse" : "Crew Boss";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/team-leads", { params: { role } });
      setLeads(data);
      if (data.length && !selected) setSelected(data[0].id);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  useEffect(() => { if (open) { setAdding(false); setEditingId(null); setForm(emptyLead); setCandQ(""); setCands([]); setToDelete(null); load(); } }, [open, load]);

  const saveLead = async () => {
    if (!form.email.trim()) { toast.error("Email requis"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/team-leads/${editingId}`, { role, ...form });
        toast.success("Responsable mis à jour");
        setAdding(false); setEditingId(null); setForm(emptyLead); setCandQ("");
        await load();
      } else {
        const { data } = await api.post("/team-leads", { role, ...form });
        toast.success("Responsable ajouté");
        setAdding(false); setForm(emptyLead); setCandQ("");
        await load();
        setSelected(data.id);
      }
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const startEdit = (l) => {
    setEditingId(l.id);
    setForm({ nom: l.nom || "", prenom: l.prenom || "", email: l.email || "", telephone: l.telephone || "" });
    setCandQ(""); setCands([]); setAdding(true);
  };

  const startAdd = () => {
    setEditingId(null); setForm(emptyLead); setCandQ(""); setCands([]); setAdding(true);
  };

  const doDelete = async () => {
    try {
      await api.delete(`/team-leads/${toDelete.id}`);
      toast.success("Responsable supprimé");
      if (selected === toDelete.id) setSelected("");
      setToDelete(null);
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const send = async () => {
    if (!selected) { toast.error("Sélectionnez un responsable"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/concerts/${concertId}/send-team-request`, { role, lead_id: selected });
      toast.success(`Demande envoyée à ${data.sent_to}`);
      onOpenChange(false);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const nameOf = (l) => [l.prenom, l.nom].filter(Boolean).join(" ").trim() || l.email;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="team-request-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Envoyer la demande d'équipe — {label}</DialogTitle>
          <DialogDescription>Choisissez un {label} existant ou ajoutez-en un, puis envoyez la demande.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[45vh] overflow-y-auto pr-1">
          {leads.length === 0 && !adding && (
            <p className="text-sm text-muted-foreground">Aucun {label} enregistré. Ajoutez-en un ci-dessous.</p>
          )}
          {leads.map((l) => (
            <div key={l.id} className={`flex items-center gap-3 rounded-lg border p-3 transition-colors ${selected === l.id ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/40"}`}
              data-testid={`team-lead-${l.id}`}>
              <input type="radio" name="lead" checked={selected === l.id} onChange={() => setSelected(l.id)} className="accent-amber-500 cursor-pointer" data-testid={`team-lead-select-${l.id}`} />
              <button type="button" className="flex-1 min-w-0 text-left" onClick={() => setSelected(l.id)}>
                <div className="text-sm font-medium">{nameOf(l)}</div>
                <div className="text-xs text-muted-foreground truncate">{l.email}{l.telephone ? ` · ${l.telephone}` : ""}</div>
              </button>
              {selected === l.id && <Check className="h-4 w-4 text-primary shrink-0" />}
              <button type="button" onClick={() => startEdit(l)} className="text-muted-foreground hover:text-primary p-1" data-testid={`edit-lead-${l.id}`}>
                <Pencil className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => setToDelete(l)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`delete-lead-${l.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        {adding ? (
          <div className="rounded-lg border border-border p-3 space-y-3">
            <div className="relative">
              <Label className="text-xs">Rechercher dans la base ({role === "chef_hotesse" ? "hôtes/ses" : "intermittents"})</Label>
              <Input value={candQ} autoComplete="off" placeholder="Tapez un nom..." data-testid="lead-search"
                onChange={(e) => fetchCands(e.target.value)}
                onFocus={() => candQ && setCandOpen(true)}
                onBlur={() => setTimeout(() => setCandOpen(false), 150)} />              {candOpen && cands.length > 0 && (
                <div className="absolute z-[70] mt-1 w-full max-h-48 overflow-auto rounded-md border border-border bg-popover shadow-xl" data-testid="lead-search-list">
                  {cands.map((cd, i) => (
                    <button type="button" key={i} className="w-full text-left px-3 py-1.5 text-sm hover:bg-secondary"
                      onMouseDown={(e) => { e.preventDefault(); pickCandidate(cd); }} data-testid={`lead-search-option-${i}`}>
                      <span className="font-medium">{[cd.nom, cd.prenom].filter(Boolean).join(" ")}</span>
                      {cd.email ? <span className="text-xs text-muted-foreground"> · {cd.email}</span> : null}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Nom</Label><Input value={form.nom} onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))} data-testid="lead-nom" /></div>
              <div><Label className="text-xs">Prénom</Label><Input value={form.prenom} onChange={(e) => setForm((f) => ({ ...f, prenom: e.target.value }))} data-testid="lead-prenom" /></div>
              <div><Label className="text-xs">Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="lead-email" /></div>
              <div><Label className="text-xs">Téléphone</Label><Input value={form.telephone} onChange={(e) => setForm((f) => ({ ...f, telephone: e.target.value }))} data-testid="lead-telephone" /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setEditingId(null); }}>Annuler</Button>
              <Button size="sm" onClick={saveLead} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-lead-button">
                {saving ? "..." : (editingId ? "Enregistrer" : "Ajouter")}
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={startAdd} data-testid="add-lead-button" className="self-start">
            <UserPlus className="h-4 w-4 mr-1.5" /> Ajouter un {label}
          </Button>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Fermer</Button>
          <Button onClick={send} disabled={sending || !selected} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="send-team-request-confirm">
            <Send className="h-4 w-4 mr-1.5" /> {sending ? "Envoi..." : "Envoyer la demande"}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-lead-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce responsable ?</AlertDialogTitle>
            <AlertDialogDescription>« {toDelete && nameOf(toDelete)} » sera définitivement supprimé.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-lead">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
